import Alpine from 'alpinejs';
import { PHONE_COUNTRIES, PhoneCountry } from '../../helpers/phoneCountries';

export interface PhonePrefixStore {
    enabled: boolean;
    openCountry: boolean;
    isValidationRunned: boolean;
    defaultCountry: PhoneCountry;
    country: PhoneCountry | null;
    countries: PhoneCountry[];
    activate(): void;
    initializeInput(input: HTMLInputElement): void;
    handleInput(input: HTMLInputElement, event?: InputEvent): void;
    selectCountry(country: PhoneCountry, input: HTMLInputElement | null): void;
}

function getDefaultCountry(): PhoneCountry {
    return PHONE_COUNTRIES[0] as PhoneCountry;
}

function getCountryFromValue(value: string): PhoneCountry | null {
    const normalizedValue = value.trim();
    const countries = [...PHONE_COUNTRIES].sort((a, b) => b.prefix.length - a.prefix.length);

    return countries.find((country) => normalizedValue.startsWith(country.prefix)) || null;
}

function getNationalDigits(value: string, country: PhoneCountry | null): string {
    if (country && value.trim().startsWith(country.prefix)) {
        return value.trim().slice(country.prefix.length).replace(/\D/g, '');
    }

    return value.replace(/^\+\d+/, '').replace(/\D/g, '');
}

function getFrenchNationalDigits(value: string): string {
    const digits = getNationalDigits(value, getDefaultCountry());

    return digits.startsWith('0') ? digits.slice(1) : digits;
}

function formatFrenchPhone(value: string): string {
    const nationalDigits = getFrenchNationalDigits(value).slice(0, 9);
    const groups = [
        nationalDigits.slice(0, 1),
        nationalDigits.slice(1, 3),
        nationalDigits.slice(3, 5),
        nationalDigits.slice(5, 7),
        nationalDigits.slice(7, 9),
    ].filter(Boolean);

    return `${getDefaultCountry().prefix}${groups.length ? ` ${groups.join(' ')}` : ' '}`;
}

function formatKnownCountryPhone(value: string, country: PhoneCountry): string {
    if (country.code === 'FR') {
        return formatFrenchPhone(value);
    }

    const nationalDigits = getNationalDigits(value, country);

    return `${country.prefix}${nationalDigits ? ` ${nationalDigits}` : ' '}`;
}

function shouldFormatKnownCountryPhone(value: string, country: PhoneCountry): boolean {
    return getNationalDigits(value, country).length > 0;
}

function isDeleteInput(event?: InputEvent): boolean {
    return event?.inputType === 'deleteContentBackward' || event?.inputType === 'deleteContentForward';
}

function moveCaretToEnd(input: HTMLInputElement): void {
    window.requestAnimationFrame(() => {
        input.setSelectionRange(input.value.length, input.value.length);
    });
}

export function formatPhoneForCrm(rawValue: string, country?: PhoneCountry | null): string {
    const detectedCountry = country || getCountryFromValue(rawValue);

    if (detectedCountry?.code === 'FR') {
        return formatFrenchPhone(rawValue).trim();
    }
    if (detectedCountry) {
        return `${detectedCountry.prefix}${getNationalDigits(rawValue, detectedCountry)}`;
    }

    return rawValue.trim().replace(/\s+/g, '');
}

export function isPhoneInputValid(
    rawValue: string,
    enabled: boolean,
    country: PhoneCountry | null
): { valid: boolean; message: string } {
    if (rawValue.trim() === '') {
        return { valid: false, message: 'Ce champ est requis.' };
    }
    if (!enabled) {
        return rawValue.length >= 14
            ? { valid: true, message: '' }
            : { valid: false, message: 'Ce champ est requis.' };
    }
    if (!country) {
        return { valid: false, message: 'Indicatif invalide.' };
    }
    if (country.code === 'FR') {
        const ok = /^\+33 [1-9] \d{2} \d{2} \d{2} \d{2}$/.test(formatFrenchPhone(rawValue).trim());

        return ok
            ? { valid: true, message: '' }
            : { valid: false, message: 'Le numéro doit comporter 9 chiffres (ex : +33 7 99 99 99 99)' };
    }
    const digits = getNationalDigits(rawValue, country);

    return digits.length > 0
        ? { valid: true, message: '' }
        : { valid: false, message: 'Numéro invalide.' };
}

export function getPhonePrefixStore(): PhonePrefixStore {
    return Alpine.store('phonePrefix') as PhonePrefixStore;
}

export function initPhonePrefixStore(): void {
    Alpine.store('phonePrefix', {
        enabled: true,
        openCountry: false,
        isValidationRunned: false,
        defaultCountry: getDefaultCountry(),
        country: getDefaultCountry(),
        countries: PHONE_COUNTRIES,
        activate(): void {
            this.enabled = true;
        },
        initializeInput(input: HTMLInputElement): void {
            if (input.value.trim() === '') {
                input.value = `${this.defaultCountry.prefix} `;
                this.country = this.defaultCountry;
                return;
            }

            this.handleInput(input);
        },
        handleInput(input: HTMLInputElement, event?: InputEvent): void {
            if (input.value.trim() === '') {
                input.value = `${this.defaultCountry.prefix} `;
                this.country = this.defaultCountry;
                moveCaretToEnd(input);
                return;
            }

            const country = getCountryFromValue(input.value);
            this.country = country;

            if (country && isDeleteInput(event) && getNationalDigits(input.value, country).length === 0) {
                input.value = country.prefix;
                moveCaretToEnd(input);
                return;
            }

            if (country && shouldFormatKnownCountryPhone(input.value, country)) {
                input.value = formatKnownCountryPhone(input.value, country);
                moveCaretToEnd(input);
            }
        },
        selectCountry(country: PhoneCountry, input: HTMLInputElement | null): void {
            this.country = country;
            this.openCountry = false;

            if (!input) {
                return;
            }

            const currentCountry = getCountryFromValue(input.value);
            const nationalDigits = getNationalDigits(input.value, currentCountry);
            input.value = formatKnownCountryPhone(`${country.prefix}${nationalDigits}`, country);
            input.focus();
            moveCaretToEnd(input);
        },
    } as PhonePrefixStore);

    window.addEventListener('ab-phone-country-prefix', () => {
        getPhonePrefixStore().activate();
    });
}
